<script setup>
  import { ref, computed, onMounted, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";
  import { useSlideTemplatesStore } from "@stores/slideTemplates";
  import { useQuestionListStore } from "@stores/survey/reports/questionList";
  import { useSurveyStore } from "@stores/survey/survey";
  import { Card, CardContent } from "@/components/ui/card";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Info } from "lucide-vue-next";
  import ThreePanePage from "@/templates/ThreePanePage.vue";
  import ReportSlideList from "@/components/Reports/ReportBuilderV2/ReportSlideList.vue";
  import ReportSlideEditor from "@/components/Reports/ReportBuilderV2/ReportSlideEditor.vue";
  import SlideTemplatePicker from "@/components/Reports/ReportBuilderV2/SlideTemplatePicker.vue";
  import ReportEmptyState from "@/components/Reports/ReportBuilderV2/ReportEmptyState.vue";
  import { useAssistantStore } from "@/stores/assistant";

  const reportStore = useReportsV2Store();
  const templatesStore = useSlideTemplatesStore();
  const questionListStore = useQuestionListStore();

  const { currentReport, selectedSlide } = storeToRefs(reportStore);
  const { token: surveyToken } = storeToRefs(useSurveyStore());
  const assistant = useAssistantStore();

  const displaySlides = computed(() => currentReport.value?.slides || []);

  const showTemplatePicker = ref(false);
  const insertBelowSlideId = ref(null);

  onMounted(async () => {
    // Fetch questions
    await questionListStore.fetchQuestions();
    // If report is already loaded, fetch templates
    if (currentReport.value?.master_report?.token) {
      await templatesStore.fetchTemplates(
        currentReport.value.master_report.token,
        currentReport.value.report_theme_id,
        surveyToken.value,
      );
    }
  });

  // Watch for report to load and fetch templates when theme or report theme changes
  watch(
    () => [currentReport.value?.master_report?.token, currentReport.value?.report_theme_id],
    async ([themeToken, reportThemeId]) => {
      if (themeToken) {
        await templatesStore.fetchTemplates(themeToken, reportThemeId, surveyToken.value);
      }
    },
  );

  const displayedSlide = computed(() => selectedSlide.value);

  const displayedTemplate = computed(() => {
    return templatesStore.findTemplateForSlide(displayedSlide.value);
  });

  function selectSlide(slideId) {
    reportStore.selectSlide(slideId);
  }

  async function handleAddSlide(templateId) {
    if (insertBelowSlideId.value) {
      await reportStore.addSlideAfter(templateId, insertBelowSlideId.value);
      insertBelowSlideId.value = null;
    } else {
      await reportStore.addSlide(templateId);
    }
    showTemplatePicker.value = false;
  }

  async function handleDeleteSlide(slideId) {
    await reportStore.removeSlide(slideId);
  }

  async function handleReorderSlides(positions) {
    await reportStore.reorderSlides(positions);
  }

  function handleInsertBelow(slideId) {
    insertBelowSlideId.value = slideId;
    showTemplatePicker.value = true;
  }

  async function handleDuplicateSlide(slideId) {
    await reportStore.duplicateSlide(slideId);
  }

  const showEmptyState = computed(() => (currentReport.value?.slides || []).length === 0);

  // The panel owns the conversation now: it opens on the prompt and the slides
  // land when the turn completes.
  function handleGenerate(prompt) {
    assistant.openWith(prompt);
  }
</script>

<template>
  <ThreePanePage
    :cols="24"
    :span-left="6"
    :span-center="18"
    :span-right="0"
    :gap="6">
    <template #left>
      <div class="flex flex-col min-h-0 overflow-hidden h-[calc(100vh-100px)]">
        <span class="text-base font-medium flex-shrink-0 h-9 mb-2 flex items-center pl-1">Slides list</span>
        <Card class="flex-1 min-h-0 flex flex-col !gap-0 !py-0 overflow-hidden">
          <CardContent class="flex-1 min-h-0 p-0">
            <ReportSlideList
              :slides="displaySlides"
              :selected-id="selectedSlide?.id"
              @select="selectSlide"
              @delete="handleDeleteSlide"
              @add="showTemplatePicker = true"
              @reorder="handleReorderSlides"
              @insert-below="handleInsertBelow"
              @duplicate="handleDuplicateSlide" />
          </CardContent>
        </Card>
      </div>
    </template>

    <template #center>
      <div class="flex flex-col min-h-0 overflow-hidden h-[calc(100vh-100px)]">
        <span class="text-base font-medium flex-shrink-0 h-9 mb-2 flex items-center pl-1 gap-1.5">
          <template v-if="!showEmptyState">
            Edit & Preview
            <Tooltip content="Please note that this simplified preview does not fully reflect the final rendering. You can generate the PowerPoint report with the Download button.">
              <Info class="h-3.5 w-3.5 text-muted-foreground/40 hover:text-muted-foreground transition-colors" />
            </Tooltip>
          </template>
        </span>
        <Card class="flex-1 min-h-0 flex flex-col !gap-0 !py-0 overflow-hidden">
          <CardContent class="flex-1 min-h-0 p-0">
            <ReportEmptyState
              v-if="showEmptyState"
              @generate="handleGenerate" />
            <ReportSlideEditor
              v-else
              :slide="displayedSlide"
              :template="displayedTemplate" />
          </CardContent>
        </Card>
      </div>
    </template>
  </ThreePanePage>

  <!-- Template picker dialog -->
  <SlideTemplatePicker
    v-model:open="showTemplatePicker"
    @select="handleAddSlide" />
</template>
